/**
 * Page de connexion
 */

import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useAppToast } from '../contexts/AppToastContext.jsx';
import CountryPhoneInput from '../components/forms/CountryPhoneInput.jsx';
import PasswordInput from '../components/forms/PasswordInput.jsx';
import { defaultCountry } from '../data/countries.js';
import { useErrorToast } from '../hooks/useErrorToast.js';
import './Login.css';

function Login() {
  const location = useLocation();
  // Pré-remplir le téléphone si on arrive depuis une inscription « compte déjà
  // créé » (double-soumission / lien rouvert) qui redirige ici.
  const [telephone, setTelephone] = useState(location.state?.telephone || defaultCountry.phoneCode);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  useErrorToast(error);
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated } = useAuth();
  const { showSuccess } = useAppToast();
  const navigate = useNavigate();

  // Message informatif transmis par une redirection d'inscription (une seule fois).
  useEffect(() => {
    if (location.state?.infoMessage) {
      showSuccess(location.state.infoMessage);
      window.history.replaceState({}, document.title);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Un utilisateur déjà authentifié ne doit jamais voir le formulaire de connexion
  // (ex. juste après une inscription avec connexion automatique).
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handlePhoneChange = (fullPhoneNumber) => {
    setTelephone(fullPhoneNumber);
    // Effacer l'erreur si elle existe
    if (errors.telephone) {
      setErrors({ ...errors, telephone: null });
    }
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setErrors({});
    setLoading(true);

    try {
      const result = await login(telephone, password);
      if (result.success) {
        // Rediriger vers le dashboard
        navigate('/dashboard');
      } else {
        setError(result.error || 'Erreur de connexion');
      }
    } catch (err) {
      const isTimeout = err.code === 'ECONNABORTED' || err.message?.includes('timeout');
      const isNetwork = !err.response && (err.message === 'Network Error' || err.code === 'ERR_NETWORK');
      if (isTimeout || isNetwork) {
        setError('Le serveur ne répond pas. Vérifiez que le backend est démarré et réessayez.');
      } else {
        setError(err.response?.data?.error || err.message || 'Une erreur est survenue. Veuillez réessayer.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <h1>Grand-Électeur</h1>
        <h2>Connexion</h2>

        <form onSubmit={handleSubmit} aria-label="Formulaire de connexion">
          <section className="form-card" aria-labelledby="section-login">
            <div className="form-card-header">
              <span className="form-card-header-icon" aria-hidden="true">&#128274;</span>
              <h3 id="section-login" className="form-card-header-title">Identifiants</h3>
            </div>
            <div className="form-card-body">
              <div className="form-group">
                <label htmlFor="telephone">
                  Numéro de téléphone <span className="required" aria-label="requis">*</span>
                </label>
                <CountryPhoneInput
                  id="telephone"
                  value={telephone}
                  onChange={handlePhoneChange}
                  errors={errors}
                  disabled={loading}
                />
              </div>
              
              <div className="form-group">
                <PasswordInput
                  id="password"
                  name="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) {
                      setErrors({ ...errors, password: null });
                    }
                    if (error) {
                      setError('');
                    }
                  }}
                  label={
                    <>
                      Mot de passe <span className="required" aria-label="requis">*</span>
                    </>
                  }
                  placeholder="••••••••"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                  error={errors.password}
                />
              </div>
            </div>
          </section>

          <div className="form-submit-row">
            <button 
              type="submit" 
              disabled={loading} 
              className="btn-primary"
              aria-busy={loading}
              aria-label={loading ? 'Connexion en cours...' : 'Se connecter'}
            >
              {loading ? 'Connexion...' : 'Se connecter'}
            </button>
          </div>
        </form>
        
        <div className="form-footer">
          <p>
            Pas encore de compte ? <Link to="/register">S'inscrire</Link>
          </p>
          <p>
            <Link to="/forgot-password">Mot de passe oublié ?</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;
